/**
 * @file 云钱包赎回模块相关hook
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { UnStakeOption, Process, UnstakeEncodeStage } from '@/types/unstake';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import BigNumber from 'bignumber.js';
import { useFm } from '@web3/base-hooks';
import { debounce } from 'lodash-es';
import { ApproveFailType } from '@/types/common';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { postUnstakeEncode, postSaveUnstakeOrder } from '@/services/unstake';
import { postSyncOrderStatus, postBindOrderTx } from '@/services/order';
import { useWallet, useWalletId } from '@/hooks/wallet';
import { getTokenAddress } from '@/config/address';
import { useToast } from '@web3/base-hooks';
import { runAsyncSafe } from '@web3/base-utils';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { useChain } from '@/hooks/wallet';
import { GasStore } from '@/types/stake';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { providers } from 'ethers';
import { stxAuthenticate, stxSignTrancaction } from '@/utils/stacks';
import { PostUnstakeEncodeParams, UnstakeType } from '@/types/unstake';
import { VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { getUnstakeEstimateToken } from '@/services/unstake';
import { useSuiSignTransaction } from '@/containers/operation/transactionHooks/suiTransactionHook';
import { ShowType } from '@/types/account';

export const useUnstakeStage = () => {
  const [unstakeStage, setUnstakeStage] = useState(UnstakeEncodeStage.UnKnow);

  const onChangeUnstakeStage = (v: UnstakeEncodeStage) => {
    setUnstakeStage(v);
  };

  return {
    unstakeStage,
    onChangeUnstakeStage,
  };
};

/**
 * @file gas费相关hook
 */
export const useGasFee = (option: Partial<UnStakeOption>) => {
  const {
    unstakeType,
    unstakeAmount,
    selectCoinList,
    process,
    showType,
    onChangeUnstakeStage,
  } = option as UnStakeOption;
  const [gasAmount, setGasAmount] = useState('');
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const { coinInfoStore } = useCoinInfoContext();
  const { poolInfo } = usePoolInfoContext();
  const [loopPercent, setLoopPercent] = useState(0);
  const { toastWarn } = useToast();
  const timer = useRef<NodeJS.Timeout>();
  const address = getTokenAddress(poolInfo?.chainCode);
  const gasToken = coinInfoStore?.[`${poolInfo?.chainCode}${address}`];
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo?.chainCode);
  const gasFeePrice = BigNumber(gasAmount || 0)
    .times(gasToken?.price)
    .toString();
  const uid = useRef(0);
  const gasStore = {
    hasGasFee: !!gasAmount,
    amount: gasAmount || '0',
    price: gasFeePrice || '0',
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    loopPercent: loopPercent * 20,
  } as GasStore;

  const fetchGasFee = async (
    selfUid: number,
    selfUnstakeAmount: string,
    selfSelectCoinList: string[]
  ) => {
    setGasFeeIsLoading(true);
    const params: PostUnstakeEncodeParams = {
      chainId: +poolInfo?.chainId,
      lpTokens: selfUnstakeAmount,
      poolId: poolInfo?.id,
      slippage: '0.5',
      unstakeTokens: selfSelectCoinList,
      walletAddress,
      pubKey,
      unstakeType:
        showType === ShowType.DelayUnStake ||
        showType === ShowType.ConfimDelayUnStake
          ? 1
          : 0,
    };
    if (poolInfo.chainCode === 'SOL') {
      const { min } = poolInfo?.poolExtendDTO?.unstakeRange || {};
      // 赎回金额小于最小赎回金额时，不请求接口
      if (min && BigNumber(selfUnstakeAmount || 0)?.lt(min)) {
        return;
      }
      const { success, result: unstakeEstimateResult } =
        await getUnstakeEstimateToken({
          chainId: +poolInfo?.chainId,
          poolId: poolInfo?.id,
          lpTokens: selfUnstakeAmount,
          type: unstakeType,
          unstakeTokens: (unstakeType === UnstakeType.Single
            ? poolInfo?.singleCoins ?? []
            : poolInfo?.multipleCoins ?? []
          ).map((item) => item?.contractAddress),
          slippage: '',
          walletAddress,
          unstakeType:
            showType === ShowType.DelayUnStake ||
            showType === ShowType.ConfimDelayUnStake
              ? 1
              : 0,
        });
      if (success) {
        const contractAddress = selfSelectCoinList[0];
        const estimate = unstakeEstimateResult?.tokenAmounts?.filter(
          (coinAmountInfo) => {
            return coinAmountInfo?.contractAddress === contractAddress;
          }
        )[0];

        params.slippage = '';
        params.minOutAmount = estimate?.amount;
        params.router = estimate?.router ?? '';
      }
    }
    const { success, result, message } = await postUnstakeEncode(params);

    if (selfUid !== uid.current) return;

    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }
    const data = result?.txDataList[0];

    setGasAmount(data?.gasFee);
    onChangeUnstakeStage(result.stage);

    return true;
  };

  const startLoopGasFee = useCallback(
    debounce(
      async (
        percent = 0,
        selfUnstakeAmount: string,
        selfSelectCoinList: string[]
      ) => {
        clearTimeout(timer.current);
        if (
          BigNumber(selfUnstakeAmount).isNaN() ||
          BigNumber(selfUnstakeAmount).eq(0)
        )
          return;

        setLoopPercent(percent);

        if (percent === 0) {
          uid.current = Math.random();
          const result = await fetchGasFee(
            uid.current,
            selfUnstakeAmount,
            selfSelectCoinList
          );
          // 报错则取消轮循
          if (!result) return;
        }

        timer.current = setTimeout(() => {
          if (percent === 4) {
            setLoopPercent(5);
            startLoopGasFee(0, selfUnstakeAmount, selfSelectCoinList);
          } else {
            startLoopGasFee(percent + 1, selfUnstakeAmount, selfSelectCoinList);
          }
        }, 2000);
      },
      500
    ),
    []
  );

  // 每次更改输入框则清空gasfee
  useEffect(() => {
    setGasAmount('');
  }, [unstakeAmount, selectCoinList]);

  useEffect(() => {
    if (process !== Process.None) {
      clearTimeout(timer.current);
      return;
    }
    startLoopGasFee(0, unstakeAmount, selectCoinList);
    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [selectCoinList, startLoopGasFee, unstakeAmount, process]);

  return {
    gasStore,
  };
};

/**
 * 按钮状态相关hook
 */
export const useButtonStatus = (option: Partial<UnStakeOption>) => {
  const {
    showType,
    unstakeAmount,
    slippageIsLost,
    gasStore,
    unstakeStage,
    confirmButtonIsLoading,
    isSolLoss = false,
    onChangeProcess,
    onChangeShowType,
  } = option as UnStakeOption;
  const fm = useFm();
  const { poolInfo } = usePoolInfoContext();
  const textIsError = useRef(false);

  const onChangeTextIsError = (v: boolean) => {
    textIsError.current = v;
  };

  const buttonText = (() => {
    if (BigNumber(unstakeAmount).isNaN() || BigNumber(unstakeAmount).eq(0)) {
      return fm('remove_liquidity');
    }
    if (
      unstakeStage === UnstakeEncodeStage.ApproveAmount &&
      !confirmButtonIsLoading
    ) {
      return fm('btn_approve');
    }
    if (
      unstakeStage === UnstakeEncodeStage.ApproveAmount &&
      confirmButtonIsLoading
    ) {
      return fm('btn_approveing');
    }
    if (
      unstakeStage === UnstakeEncodeStage.ApproveTrade &&
      !confirmButtonIsLoading
    ) {
      return fm('redeem');
    }
    if (
      unstakeStage === UnstakeEncodeStage.ApproveTrade &&
      confirmButtonIsLoading
    ) {
      return fm('redeemingInProgress');
    }
    return fm('remove_liquidity');
  })();

  const buttonDisabled = (() => {
    if (BigNumber(unstakeAmount || 0).lte(0)) {
      return true;
    }

    if (!gasStore?.hasGasFee) {
      return true;
    }

    if (textIsError.current) {
      return true;
    }
  })();

  const onClickUnstake = () => {
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPageredeem',
      button_name:
        showType === ShowType.DelayUnStake
          ? 'Redeem_Delayed'
          : 'Redeem_Confirm',
    });

    if (isSolLoss) {
      onChangeProcess(Process.SolLoss);
      return;
    }
    if (slippageIsLost) {
      onChangeProcess(Process.Slippage);
      return;
    }

    if (poolInfo.poolExtendDTO.delayTime) {
      onChangeProcess(Process.Delay);
      return;
    }
    onChangeProcess(Process.Confirm);
    onChangeShowType(
      showType === ShowType.DelayUnStake
        ? ShowType.ConfimDelayUnStake
        : ShowType.ConfirmUnstake
    );
  };

  return {
    buttonText,
    buttonDisabled,
    onClickUnstake,
    onChangeTextIsError,
  };
};

/**
 * 封装了一些代码行数比较多的并且可以复用的请求
 */
export const useFetchStore = (option: Partial<UnStakeOption>) => {
  const { unstakeAmount, slippage, selectCoinList } = option as UnStakeOption;
  const { poolInfo } = usePoolInfoContext();
  const { toastWarn } = useToast();
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo.chainCode);
  const fm = useFm();
  const { id } = poolInfo;

  // 生成staking订单
  const createOrder = async (stage: UnstakeEncodeStage) => {
    const { success, result } = await postSaveUnstakeOrder({
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage,
      stage,
      lpTokens: unstakeAmount,
      unstakeTokens: selectCoinList,
      walletAddress,
    });

    if (!success) {
      toastWarn(fm('mpsToastUpcoming'));
    }

    return {
      success,
      result,
    };
  };

  // 同步给订单错误态
  const updateFailedOrderStatus = async (
    isCancel: boolean,
    orderCode: string,
    orderTxId: number
  ) => {
    await postSyncOrderStatus({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      status: isCancel ? ApproveFailType.CANCEL : ApproveFailType.UN_KNOWN,
      walletAddress,
    });
  };

  // 同步给订单成功态
  const updateSuccessOrderStatus = async (
    hash: string,
    orderCode: string,
    orderTxId: number
  ) => {
    await postBindOrderTx({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      txHash: hash,
      walletAddress,
    });
  };

  // 获取编码
  const getEncodeData = async (
    minOutAmount?: string,
    router?: string,
    showType?: ShowType
  ) => {
    let success = true;
    const params: PostUnstakeEncodeParams = {
      minOutAmount,
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage: '0.5',
      lpTokens: unstakeAmount,
      unstakeTokens: selectCoinList,
      walletAddress,
      pubKey,
    };

    if (minOutAmount) {
      params.minOutAmount = minOutAmount;
    }

    if (router) {
      params.router = router;
    }

    if (showType !== undefined && showType !== null) {
      params.unstakeType = showType;
    }

    const {
      success: encodeSuccess,
      result,
      message,
    } = await postUnstakeEncode(params);

    if (!encodeSuccess) {
      toastWarn(message);
      success = false;
    }

    if (!result.txDataList[0]) {
      toastWarn(fm('systemBusyTryLater'));
      success = false;
    }

    return {
      success,
      result,
    };
  };

  return {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  };
};

/**
 * 赎回操作相关状态
 */
export const useUnstake = (option: Partial<UnStakeOption>) => {
  const {
    selectCoinList,
    unstakeCoinAmountStore,
    process,
    unstakeAmount,
    slippage,
    showType,
    onChangeProcess,
    onChangeUnstakeStage,
  } = option as UnStakeOption;
  const { poolInfo } = usePoolInfoContext();
  const { toastWarn, toastSuccess } = useToast();
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const { switchToPoolChain } = useChain();
  const [orderCode, setOrderCode] = useState('');
  const { evmThirdPartyProvider, solanaThirdPartyProvider } = useWallet();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const fm = useFm();
  const {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  } = useFetchStore(option);
  const walletId = useWalletId();
  const { currentWalletType } = useWallet();
  const { suiSignAndTransaction } = useSuiSignTransaction();

  const evmUnstake = async () => {
    // 切链
    const switchChainIsSuccess = await switchToPoolChain(poolInfo.chainId);
    if (!switchChainIsSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    let txHash = '';

    try {
      const txData = result.txDataList[0];
      const provider = new providers.Web3Provider(evmThirdPartyProvider);
      const signer = provider.getSigner();
      // 签名
      txHash = (
        await signer.sendTransaction({
          from: txData.from,
          to: txData.to,
          data: txData.data,
          value: txData.value,
        })
      ).hash;
    } catch (e: any) {
      // 是否为手动拒绝
      const isCancel = e?.code?.includes?.('ACTION_REJECTED');

      if (!isCancel) {
        toastWarn(fm('systemBusyTryLater'));
      }

      updateFailedOrderStatus(isCancel, orderCode, orderTxId);
      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    // 结束
    if (result.stage === UnstakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === UnstakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeUnstakeStage(UnstakeEncodeStage.ApproveTrade);
    }
  };

  const stacksUnstake = async () => {
    // 授权
    const { success: authSuccess } = await stxAuthenticate(walletAddress);

    if (!authSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    const { success: signSuccess, hash: txHash } = await stxSignTrancaction(
      result.txDataList[0].data
    );

    if (!signSuccess) {
      updateFailedOrderStatus(true, orderCode, orderTxId);
      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    // 结束
    if (result.stage === UnstakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === UnstakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeUnstakeStage(UnstakeEncodeStage.ApproveTrade);
    }
  };

  const solUnStake = async () => {
    // 获取LP预估
    const miniOutAmount = unstakeCoinAmountStore[selectCoinList[0]]?.amount;
    const router = unstakeCoinAmountStore[selectCoinList[0]]?.router ?? '';
    const actualShowTyp =
      showType === ShowType.DelayUnStake ||
      showType === ShowType.ConfimDelayUnStake
        ? 1
        : 0;
    const { success: encodeSuccess, result } = await getEncodeData(
      miniOutAmount,
      router,
      actualShowTyp
    );

    if (!encodeSuccess) return;

    const provider = solanaThirdPartyProvider;

    const transaction = VersionedTransaction.deserialize(
      bs58.decode(result.txDataList[0].data)
    );

    const [errTx, tx] = await runAsyncSafe<
      { signatures: Array<Uint8Array>; serialize: () => Uint8Array },
      Error
    >(provider.signTransaction(transaction));
    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      return;
    }

    const txDataList = result.txDataList[0];
    const { success, result: OrderResult } = await postSaveUnstakeOrder({
      chainId: +poolInfo.chainId,
      poolId: poolInfo.id,
      slippage,
      stage: result.stage,
      lpTokens: unstakeAmount,
      unstakeTokens: selectCoinList,
      walletAddress,
      orderCode: result.orderCode,
      broadcast: 1,
      broadcastData: [
        {
          signData: bs58.encode(tx.serialize()),
          chainId: +poolInfo.chainId,
          from: txDataList?.from ?? '',
          to: txDataList?.to ?? '',
          value: txDataList?.value ?? '',
          txHash: bs58.encode(tx.signatures[0]),
        },
      ],
      walletId: walletId?.walletId,
      walletType: currentWalletType,
      unstakeType: actualShowTyp,
    });

    if (!success) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }

    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(OrderResult.orderCode);
    }
  };

  const suiUnstake = async () => {
    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }
    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    // 钱包签名
    const [errTx, transactionResult] = await suiSignAndTransaction(
      result.txDataList[0].data
    );

    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      // 同步订失败状态
      updateFailedOrderStatus(isReject, orderCode, orderTxId);
      return;
    }
    // 同步订单成功状态
    updateSuccessOrderStatus(transactionResult.digest, orderCode, orderTxId);

    // // 结束
    if (result.stage === UnstakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === UnstakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeUnstakeStage(UnstakeEncodeStage.ApproveTrade);
    }
  };

  useEffect(() => {
    if (process === Process.None) {
      setConfirmButtonIsLoading(false);
    }
  }, [process]);

  // 开始赎回
  const beginUnstake = async () => {
    setConfirmButtonIsLoading(true);

    if (poolInfo.chainCode === 'STX') {
      await stacksUnstake();
    } else if (poolInfo.chainCode === 'SOL') {
      await solUnStake();
    } else if (poolInfo.chainCode === 'SUI') {
      await suiUnstake();
    } else {
      await evmUnstake();
    }

    setConfirmButtonIsLoading(false);
  };

  return {
    orderCode,
    confirmButtonIsLoading,
    beginUnstake,
  };
};
